import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import socketService from '../services/socket';
import '../styles/MyJoinRequests.css';

interface MyJoinRequest {
  id: number;
  room_id: number;
  room_code: string;
  dictionary_name: string;
  status: string;
  responder_username?: string;
  created_at: string;
  responded_at?: string;
}

interface MyJoinRequestsProps {
  onAcceptedJoin?: (room: any, participants: any[]) => void;
}

const MyJoinRequests: React.FC<MyJoinRequestsProps> = ({ onAcceptedJoin }) => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<MyJoinRequest[]>([]);
  const [notifications, setNotifications] = useState<Array<{ message: string; type: 'success' | 'error' }>>([]);
  const [showAcceptedModal, setShowAcceptedModal] = useState(false);
  const [acceptedData, setAcceptedData] = useState<any>(null);
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    if (!user) return;

    fetchMyRequests();

    // Listen for responses to my requests
    socketService.on('join_request_response', handleRequestResponse);
    socketService.on('join_request_accepted', handleJoinAccepted);

    return () => {
      socketService.off('join_request_response', handleRequestResponse);
      socketService.off('join_request_accepted', handleJoinAccepted);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchMyRequests = async () => {
    try {
      const data = await apiService.get('/api/room-browser/my-requests');
      setRequests(data.requests || []);
    } catch (err: any) {
      console.error('Error fetching my requests:', err);
    }
  };

  const handleRequestResponse = (data: any) => {
    console.log('📨 Join request REJECTION received:', data);

    const message = `✗ ${data.responderUsername} rejected your request.`;
    const type = 'error';

    // Show notification
    setNotifications(prev => [...prev, { message, type }]);

    // Update request status
    setRequests(prev => prev.map(req =>
      req.id === data.requestId
        ? { ...req, status: data.action, responder_username: data.responderUsername }
        : req
    ));

    // Remove notification after 8 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.message !== message));
    }, 8000);

    // Refresh list
    fetchMyRequests();
  };

  const handleJoinAccepted = (data: any) => {
    console.log('🎉 Join request accepted event received:', data);
    console.log('onAcceptedJoin callback exists:', !!onAcceptedJoin);
    console.log('room data:', data.room);
    console.log('participants:', data.participants);

    // Update request status
    setRequests(prev => prev.filter(req => req.id !== data.requestId));

    // Show full-screen acceptance modal with countdown
    setAcceptedData(data);
    setShowAcceptedModal(true);
    setCountdown(3);
  };

  // Countdown effect for accepted modal
  useEffect(() => {
    if (!showAcceptedModal || countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          // Countdown finished, join the room
          clearInterval(timer);
          setShowAcceptedModal(false);

          if (onAcceptedJoin && acceptedData?.room && acceptedData?.participants && user) {
            console.log('✅ Auto-joining room via callback...');
            // Join via socket
            console.log('📡 Joining socket room:', acceptedData.roomId);
            socketService.joinRoom(acceptedData.roomId, user.id, user.username);

            // Update stage to 'lobby'
            socketService.getSocket()?.emit('update_user_stage', {
              roomId: acceptedData.roomId,
              userId: user.id,
              username: user.username,
              stage: 'lobby'
            });

            // Trigger the room joined callback
            console.log('🔄 Triggering onRoomJoined callback');
            onAcceptedJoin(acceptedData.room, acceptedData.participants);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showAcceptedModal, countdown, onAcceptedJoin, acceptedData, user]);

  const handleJoinRoom = async (roomId: number, roomCode: string) => {
    console.log('🔗 Manual join room clicked:', { roomId, roomCode });

    if (!user) {
      console.error('❌ No user logged in');
      return;
    }

    try {
      // Fetch room details (this includes participants)
      const roomData = await apiService.get(`/api/competitive/room/${roomCode}`);
      const room = roomData.room;
      const participants = roomData.participants || [];

      if (!room) {
        console.error('❌ Room not found');
        return;
      }

      console.log('✅ Room data fetched:', room);
      console.log('✅ Participants:', participants);

      // Join via socket
      socketService.joinRoom(room.id, user.id, user.username);

      // Update stage to 'lobby'
      socketService.getSocket()?.emit('update_user_stage', {
        roomId: room.id,
        userId: user.id,
        username: user.username,
        stage: 'lobby'
      });

      // Trigger callback to show lobby
      if (onAcceptedJoin) {
        console.log('🔄 Triggering onAcceptedJoin callback from manual join');
        onAcceptedJoin(room, participants);
      } else {
        console.error('❌ No onAcceptedJoin callback available');
      }
    } catch (err: any) {
      console.error('❌ Error joining room:', err);
    }
  };

  const handleCancelRequest = async (requestId: number) => {
    try {
      await apiService.delete(`/api/room-browser/cancel-request/${requestId}`);
      setRequests(prev => prev.filter(req => req.id !== requestId));
    } catch (err: any) {
      console.error('Error canceling request:', err);
    }
  };

  if (requests.length === 0 && notifications.length === 0 && !showAcceptedModal) {
    return null;
  }

  return (
    <>
      {/* Full-screen acceptance modal */}
      {showAcceptedModal && acceptedData && (
        <div className="acceptance-modal-overlay">
          <div className="acceptance-modal">
            <div className="acceptance-icon">✓</div>
            <h2 className="acceptance-title">Request Accepted!</h2>
            <p className="acceptance-message">
              {acceptedData.responderUsername} accepted your request to join <strong>{acceptedData.roomCode}</strong>
            </p>
            <div className="countdown-circle">
              <div className="countdown-number">{countdown}</div>
            </div>
            <p className="countdown-text">Joining room in {countdown} second{countdown !== 1 ? 's' : ''}...</p>
          </div>
        </div>
      )}

      <div className="my-join-requests">
        {/* Notifications */}
        {notifications.map((notif, index) => (
          <div key={index} className={`request-notification ${notif.type}`}>
            {notif.message}
          </div>
        ))}

      {/* Pending Requests */}
      {requests.length > 0 && (
        <div className="my-requests-panel">
          <h4 className="panel-title">📤 My Join Requests</h4>
          <div className="my-requests-list">
            {requests.map(request => (
              <div key={request.id} className={`my-request-item ${request.status}`}>
                <div className="request-room-info">
                  <span className="room-code-badge">{request.room_code}</span>
                  <span className="room-topic">{request.dictionary_name}</span>
                </div>
                <div className="request-status-info">
                  {request.status === 'pending' && (
                    <>
                      <span className="status-pending">⏳ Waiting for response...</span>
                      <button
                        className="cancel-request-btn"
                        onClick={() => handleCancelRequest(request.id)}
                      >
                        Cancel
                      </button>
                    </>
                  )}
                  {request.status === 'accepted' && (
                    <>
                      <span className="status-accepted">
                        ✓ Accepted by {request.responder_username}
                      </span>
                      <button
                        className="join-room-btn"
                        onClick={() => handleJoinRoom(request.room_id, request.room_code)}
                      >
                        Join Room
                      </button>
                    </>
                  )}
                  {request.status === 'rejected' && (
                    <span className="status-rejected">
                      ✗ Rejected by {request.responder_username}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default MyJoinRequests;
